import type { ComboboxItem } from '@/components'
import {
  Card,
  Combobox,
  DatePicker,
  FAIcon,
  Field,
  FieldLabel,
  LeaderGroupCombobox,
  UserCombobox,
} from '@/components'
import { cn } from '@/lib/utils'
import type { Module } from '@/modules/companies'
import { useTranslate, type UpdateFormFn } from '@ur-solutions/react-hooks'
import { Activity, useMemo, useState } from 'react'
import { isMobileOnly } from 'react-device-detect'
import { InfringementSeverities } from '../../consts'
import type {
  InfringementsTableFilter,
  InfringementStatus,
  SeverityCategory,
} from '../../types'
import {
  useInfringementCategoryFunctions,
  useInfringementSeverityFunctions,
} from '../../util'
import type { CombinedInfringementSource } from '../table/combined-rows'
import {
  categoryGroups,
  excludedIdentifiers,
  selectedGroupKeys,
} from './category-groups'

const STATUSES: InfringementStatus[] = ['OPEN', 'STARTED', 'CLOSED', 'ARCHIVED']
const SEVERITIES = Object.keys(InfringementSeverities) as SeverityCategory[]
// Working time rules carry no severity grade.
const SOURCES_WITH_SEVERITY: CombinedInfringementSource[] = [
  'DRIVING_REST',
  'TACHOGRAPH_USE',
]

interface Props {
  filter: InfringementsTableFilter
  updateForm: UpdateFormFn<InfringementsTableFilter>
  module: Module
  leaderGroups: { id: string; users: { id: string }[] }[]
  canViewOthers: boolean
  /** The types this company and user can list; the type filter only shows
   * when there is more than one. */
  availableSources: CombinedInfringementSource[]
  /** The types picked in the type filter; none picked means all. */
  pickedSources: CombinedInfringementSource[]
}

/** The infringement list's filters, laid out like the Absence page's. */
export function InfringementsFilters({
  filter,
  updateForm,
  module,
  leaderGroups,
  canViewOthers,
  availableSources,
  pickedSources,
}: Props) {
  const translations = useTranslate({
    filter: 'common.filter',
    users: 'infringements.filters.users',
    leaderGroup: 'infringements.filters.leader-group',
    dateRange: 'infringements.filters.date-range',
    sourcesLabel: 'infringements.filters.sources',
    status: 'common.status',
    severities: 'infringements.severities',
    categories: 'common.categories',
    select: 'common.select-ellipsis',
    all: 'common.all',
    statuses: {
      OPEN: 'common.open-alt',
      STARTED: 'issues.started',
      CLOSED: 'common.closed',
      ARCHIVED: 'common.archived',
    },
    sources: {
      DRIVING_REST: 'infringements.source.driving-rest',
      WORKING_TIME: 'infringements.source.working-time',
      TACHOGRAPH_USE: 'infringements.source.tachograph-use',
    },
    groups: {
      'W-overwork': 'infringements.work-day-category.overwork',
      'W-pause': 'infringements.work-day-category.pause',
      'T-country': 'infringements.category-group.country-codes',
      'T-manual': 'infringements.category-group.manual-entries',
    },
  })
  const { getSeverityTranslation } = useInfringementSeverityFunctions()
  const { getCategoryTranslation, getCategoriesFromIdentifier } =
    useInfringementCategoryFunctions()
  const [collapsed, setCollapsed] = useState(isMobileOnly)
  const showFilters = !isMobileOnly || !collapsed

  const sources = filter.sources ?? ['DRIVING_REST']
  const groupsBySource = useMemo(
    () =>
      categoryGroups({
        B: getCategoriesFromIdentifier('B'),
        C: getCategoriesFromIdentifier('C'),
        D: getCategoriesFromIdentifier('D'),
      }),
    [getCategoriesFromIdentifier]
  )
  const groups = sources.flatMap(source => groupsBySource[source])
  const groupItems: ComboboxItem<string>[] = groups.map(group => ({
    value: group.key,
    label:
      group.key === 'B' || group.key === 'C' || group.key === 'D'
        ? getCategoryTranslation(group.key)
        : translations.groups[group.key],
    group: sources.length > 1 ? translations.sources[group.source] : undefined,
  }))

  const leaderGroupUsers = useMemo(
    () =>
      new Set(
        leaderGroups
          .find(group => group.id === filter.leaderGroup)
          ?.users.map(user => user.id) ?? []
      ),
    [filter.leaderGroup, leaderGroups]
  )

  const sourceItems: ComboboxItem<CombinedInfringementSource>[] =
    availableSources.map(source => ({
      value: source,
      label: translations.sources[source],
    }))
  const statusItems: ComboboxItem<InfringementStatus>[] = STATUSES.map(
    status => ({ value: status, label: translations.statuses[status] })
  )
  const severityItems: ComboboxItem<SeverityCategory>[] = SEVERITIES.map(
    severity => ({ value: severity, label: getSeverityTranslation(severity) })
  )
  const selectedSeverities =
    filter.excludeSeverities.length === 0
      ? []
      : SEVERITIES.filter(
          severity => !filter.excludeSeverities.includes(severity)
        )

  return (
    <Card className="mb-4 p-0" contentClassName="p-0">
      {isMobileOnly && (
        <button
          type="button"
          className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left text-sm font-semibold text-stone-600"
          aria-expanded={showFilters}
          onClick={() => setCollapsed(value => !value)}
        >
          {translations.filter}
          <FAIcon
            className={cn('transition-transform', showFilters && 'rotate-180')}
            icon="chevron-down"
          />
        </button>
      )}

      {showFilters && (
        <form className="grid gap-4 p-4 md:flex md:flex-wrap md:justify-start">
          <Activity mode={canViewOthers ? 'visible' : 'hidden'}>
            <Field>
              <FieldLabel>{translations.users}</FieldLabel>
              <UserCombobox
                value={filter.users}
                className="max-w-[350px]"
                mode="multiple"
                placeholder={translations.select}
                hideItem={item =>
                  !!filter.leaderGroup && !leaderGroupUsers.has(item.id)
                }
                onChange={value => updateForm({ users: value })}
              />
            </Field>
          </Activity>

          <Activity
            mode={
              canViewOthers && leaderGroups.length > 0 ? 'visible' : 'hidden'
            }
          >
            <Field>
              <FieldLabel>{translations.leaderGroup}</FieldLabel>
              <LeaderGroupCombobox
                mode="single"
                module={module}
                value={filter.leaderGroup ?? ''}
                clearable
                placeholder={translations.select}
                onChange={value =>
                  updateForm({ leaderGroup: value || null, users: [] })
                }
              />
            </Field>
          </Activity>

          <Field>
            <FieldLabel>{translations.dateRange}</FieldLabel>
            <DatePicker
              mode="range"
              value={filter.dateRange}
              format="PP"
              onChange={value => {
                if (!value?.from || !value.to) return
                updateForm({ dateRange: { from: value.from, to: value.to } })
              }}
            />
          </Field>

          <Activity mode={availableSources.length > 1 ? 'visible' : 'hidden'}>
            <Field>
              <FieldLabel>{translations.sourcesLabel}</FieldLabel>
              <Combobox<CombinedInfringementSource>
                mode="multiple"
                className="max-w-[350px]"
                noSearch
                placeholder={translations.all}
                value={pickedSources}
                items={sourceItems}
                onChange={value =>
                  updateForm({
                    sources:
                      value.length === availableSources.length ? [] : value,
                  })
                }
              />
            </Field>
          </Activity>

          <Field>
            <FieldLabel>{translations.status}</FieldLabel>
            <Combobox<InfringementStatus>
              mode="multiple"
              className="max-w-[350px]"
              noSearch
              placeholder={translations.all}
              value={filter.statuses}
              items={statusItems}
              onChange={value => updateForm({ statuses: value })}
            />
          </Field>

          <Activity
            mode={
              sources.some(source => SOURCES_WITH_SEVERITY.includes(source))
                ? 'visible'
                : 'hidden'
            }
          >
            <Field>
              <FieldLabel>{translations.severities}</FieldLabel>
              <Combobox<SeverityCategory>
                mode="multiple"
                className="max-w-[350px]"
                noSearch
                placeholder={translations.all}
                value={selectedSeverities}
                items={severityItems}
                onChange={value =>
                  updateForm({
                    excludeSeverities:
                      value.length === 0
                        ? []
                        : SEVERITIES.filter(
                            severity => !value.includes(severity)
                          ),
                  })
                }
              />
            </Field>
          </Activity>

          <Field>
            <FieldLabel>{translations.categories}</FieldLabel>
            <Combobox<string>
              mode="multiple"
              className="max-w-[350px]"
              placeholder={translations.all}
              value={selectedGroupKeys(groups, filter.excludeCategories)}
              items={groupItems}
              onChange={value =>
                updateForm({
                  excludeCategories: excludedIdentifiers(groups, value),
                })
              }
            />
          </Field>
        </form>
      )}
    </Card>
  )
}
